// frontend/src/pages/Templates/TemplateDetail.jsx
import { useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import {
  getTemplate,
  updateTemplate,
  addTemplateVersion,
  deleteTemplateVersion,
  getVersionDownloadUrl,
} from "../../api/template.api.js";
import { API_URL } from "../../api/client.js";
import {
  PageHeader,
  Spinner,
  formatDate,
  formatFileSize,
} from "../../components/ui";
import {
  ArrowLeft,
  Edit2,
  Check,
  X,
  Upload,
  Download,
  Trash2,
  FileText,
  Plus,
  Users,
  AlertTriangle,
  Eye,
} from "lucide-react";
import DocPreviewModal from "../../components/DocPreviewModal.jsx";

const CATEGORIES = ["NDA", "MOU", "PKS", "CORPORATE_ACTION", "ADDENDUM", "GENERAL"];
const AUDIENCES = ["INTERNAL", "VENDOR", "PARTNER"];
const AUDIENCE_LABELS = { INTERNAL: "Internal", VENDOR: "Vendor", PARTNER: "Partner" };

const CATEGORY_COLORS = {
  NDA: "bg-purple-50 text-purple-700 border-purple-200",
  MOU: "bg-blue-50 text-blue-700 border-blue-200",
  PKS: "bg-green-50 text-green-700 border-green-200",
  CORPORATE_ACTION: "bg-teal-50 text-teal-700 border-teal-200",
  ADDENDUM: "bg-orange-50 text-orange-700 border-orange-200",
  GENERAL: "bg-gray-50 text-gray-600 border-gray-200",
};

const CATEGORY_LABELS = {
  NDA: "NDA",
  MOU: "MOU",
  PKS: "PKS",
  CORPORATE_ACTION: "Corporate Action",
  ADDENDUM: "Addendum",
  GENERAL: "General",
};

function catLabel(cat) {
  return CATEGORY_LABELS[cat] ?? cat;
}

function catColor(cat) {
  return CATEGORY_COLORS[cat] ?? "bg-indigo-50 text-indigo-700 border-indigo-200";
}

const MIME_LABELS = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
};

// ─── Upload new version modal ─────────────────────────────────────────────────

function AddVersionModal({ templateId, currentVersionCount, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState(`v${currentVersionCount + 1}`);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file) return setError("File is required");
    setError("");

    const fd = new FormData();
    fd.append("file", file);
    fd.append("versionLabel", label.trim() || `v${currentVersionCount + 1}`);
    fd.append("notes", notes.trim());

    setSubmitting(true);
    try {
      const version = await addTemplateVersion(templateId, fd);
      onSuccess(version);
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Upload New Version</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Version label
            </label>
            <input
              className="input text-sm"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={`v${currentVersionCount + 1}`}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              File <span className="text-red-500">*</span>
              <span className="ml-1 text-gray-400 font-normal">(PDF or DOCX)</span>
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(e) => setFile(e.target.files[0] || null)}
            />
            {file ? (
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50">
                <FileText size={16} className="text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 truncate">{file.name}</p>
                  <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setFile(null); fileRef.current.value = ""; }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 hover:border-brand-400 hover:text-brand-600 transition-colors"
              >
                <Upload size={15} />
                Choose file
              </button>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Notes</label>
            <input
              className="input text-sm"
              placeholder="What changed in this version? (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary flex-1 text-sm flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                "Upload"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function TemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMaster } = useAuth();
  const qc = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);
  const [categoryIsCustom, setCategoryIsCustom] = useState(false);
  const [showAddVersion, setShowAddVersion] = useState(false);
  const [deletingVersionId, setDeletingVersionId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(null); // { id, fileName, mimeType, fileSize }

  const { data: template, isLoading } = useQuery({
    queryKey: ["template", id],
    queryFn: () => getTemplate(id),
  });

  function startEdit() {
    setCategoryIsCustom(!CATEGORIES.includes(template.category));
    setEditForm({
      title: template.title,
      description: template.description ?? "",
      category: template.category,
      audience: [...template.audience],
      isActive: template.isActive,
    });
    setEditing(true);
    setEditError("");
  }

  function cancelEdit() {
    setEditing(false);
    setEditForm(null);
    setEditError("");
    setCategoryIsCustom(false);
  }

  function toggleAudience(a) {
    setEditForm((f) => ({
      ...f,
      audience: f.audience.includes(a)
        ? f.audience.filter((x) => x !== a)
        : [...f.audience, a],
    }));
  }

  async function saveEdit() {
    if (!editForm.title.trim()) return setEditError("Title is required");
    if (editForm.audience.length === 0) return setEditError("Select at least one audience");

    setSaving(true);
    setEditError("");
    try {
      await updateTemplate(id, editForm);
      qc.invalidateQueries({ queryKey: ["template", id] });
      setEditing(false);
    } catch (err) {
      setEditError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteVersion(versionId) {
    if (!window.confirm("Delete this version? This cannot be undone.")) return;
    setDeletingVersionId(versionId);
    try {
      await deleteTemplateVersion(id, versionId);
      qc.invalidateQueries({ queryKey: ["template", id] });
    } catch (err) {
      alert(err.message || "Delete failed");
    } finally {
      setDeletingVersionId(null);
    }
  }

  async function handleDownload(versionId, fileName) {
    setDownloadingId(versionId);
    try {
      const { url } = await getVersionDownloadUrl(id, versionId);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.target = "_blank";
      a.click();
    } catch (err) {
      alert(err.message || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  }

  if (isLoading) return <Spinner />;
  if (!template) return (
    <div className="text-sm text-gray-500 text-center py-12">Template not found.</div>
  );

  const activeVersions = template.versions ?? [];
  const f = editForm ?? {};

  return (
    <div>
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="mb-6">
        <Link
          to="/templates"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
        >
          <ArrowLeft size={14} /> Template Dokumen
        </Link>

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {editing ? (
              <input
                className="input text-lg font-semibold w-full"
                value={f.title}
                onChange={(e) => setEditForm((x) => ({ ...x, title: e.target.value }))}
              />
            ) : (
              <h1 className="text-xl font-bold text-gray-900">{template.title}</h1>
            )}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded border ${catColor(template.category)}`}
              >
                {catLabel(template.category)}
              </span>
              {!template.isActive && (
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-400 border border-gray-200">
                  Inactive
                </span>
              )}
              {template.audience.map((a) => (
                <span key={a} className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  <Users size={10} />
                  {AUDIENCE_LABELS[a]}
                </span>
              ))}
            </div>
          </div>

          {isMaster && (
            editing ? (
              <div className="flex items-center gap-2">
                <button onClick={cancelEdit} className="btn-secondary text-sm flex items-center gap-1.5">
                  <X size={14} /> Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="btn-primary text-sm flex items-center gap-1.5"
                >
                  {saving ? (
                    <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check size={14} />
                  )}
                  Save
                </button>
              </div>
            ) : (
              <button onClick={startEdit} className="btn-secondary text-sm flex items-center gap-1.5">
                <Edit2 size={14} /> Edit
              </button>
            )
          )}
        </div>
      </div>

      {/* ── Edit form fields ─────────────────────────────────────────────── */}
      {editing && (
        <div className="card p-5 mb-6 space-y-4">
          {editError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {editError}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Description</label>
            <textarea
              className="input text-sm resize-none"
              rows={2}
              value={f.description}
              onChange={(e) => setEditForm((x) => ({ ...x, description: e.target.value }))}
              placeholder="Brief description..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Category</label>
              <select
                className="input text-sm"
                value={categoryIsCustom ? "_custom" : f.category}
                onChange={(e) => {
                  if (e.target.value === "_custom") {
                    setCategoryIsCustom(true);
                    setEditForm((x) => ({ ...x, category: "" }));
                  } else {
                    setCategoryIsCustom(false);
                    setEditForm((x) => ({ ...x, category: e.target.value }));
                  }
                }}
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
                <option value="_custom">Lainnya...</option>
              </select>
              {categoryIsCustom && (
                <input
                  className="input text-sm mt-2"
                  placeholder="Nama kategori..."
                  value={f.category}
                  onChange={(e) => setEditForm((x) => ({ ...x, category: e.target.value }))}
                  autoFocus
                />
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Status</label>
              <select
                className="input text-sm"
                value={f.isActive ? "active" : "inactive"}
                onChange={(e) => setEditForm((x) => ({ ...x, isActive: e.target.value === "active" }))}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Audience</label>
            <div className="flex gap-2 flex-wrap">
              {AUDIENCES.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => toggleAudience(a)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    f.audience?.includes(a)
                      ? "bg-brand-600 text-white border-brand-600"
                      : "bg-white text-gray-600 border-gray-200 hover:border-brand-400"
                  }`}
                >
                  {AUDIENCE_LABELS[a]}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Description (view mode) ──────────────────────────────────────── */}
      {!editing && template.description && (
        <div className="card p-5 mb-6">
          <p className="text-sm text-gray-600">{template.description}</p>
          <p className="text-xs text-gray-400 mt-3">
            Uploaded by {template.uploadedBy?.name} · {formatDate(template.createdAt)}
          </p>
        </div>
      )}

      {/* ── Version history ──────────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Riwayat Versi</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {activeVersions.length} version{activeVersions.length !== 1 ? "s" : ""}
            </p>
          </div>
          {isMaster && (
            <button
              onClick={() => setShowAddVersion(true)}
              className="btn-secondary text-sm flex items-center gap-1.5"
            >
              <Plus size={14} /> Upload Version
            </button>
          )}
        </div>

        {activeVersions.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-400">No versions.</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {activeVersions.map((v, idx) => (
              <div key={v.id} className="flex items-start gap-4 px-6 py-4">
                <div className="flex-shrink-0 mt-0.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${idx === 0 ? "bg-brand-50" : "bg-gray-50"}`}>
                    <FileText size={14} className={idx === 0 ? "text-brand-500" : "text-gray-400"} />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{v.versionLabel}</span>
                    {idx === 0 && (
                      <span className="text-xs bg-brand-50 text-brand-600 px-1.5 py-0.5 rounded font-medium">
                        Latest
                      </span>
                    )}
                    <span className="text-xs text-gray-400">
                      {MIME_LABELS[v.mimeType] ?? v.mimeType}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{v.fileName}</p>
                  {v.notes && (
                    <p className="text-xs text-gray-400 mt-1 italic">{v.notes}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {formatFileSize(v.fileSize)} · {v.uploadedBy?.name} · {formatDate(v.uploadedAt)}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setPreviewVersion({ id: v.id, fileName: v.fileName, mimeType: v.mimeType, fileSize: v.fileSize })}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                    title="Preview"
                  >
                    <Eye size={15} />
                  </button>
                  <button
                    onClick={() => handleDownload(v.id, v.fileName)}
                    disabled={downloadingId === v.id}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                    title="Download"
                  >
                    {downloadingId === v.id ? (
                      <span className="w-4 h-4 border-2 border-gray-300 border-t-brand-500 rounded-full animate-spin inline-block" />
                    ) : (
                      <Download size={15} />
                    )}
                  </button>
                  {isMaster && (
                    <button
                      onClick={() => handleDeleteVersion(v.id)}
                      disabled={deletingVersionId === v.id}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete version"
                    >
                      {deletingVersionId === v.id ? (
                        <span className="w-4 h-4 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin inline-block" />
                      ) : (
                        <Trash2 size={15} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddVersion && (
        <AddVersionModal
          templateId={id}
          currentVersionCount={activeVersions.length}
          onClose={() => setShowAddVersion(false)}
          onSuccess={() => {
            setShowAddVersion(false);
            qc.invalidateQueries({ queryKey: ["template", id] });
          }}
        />
      )}

      {previewVersion && (
        <DocPreviewModal
          fileName={previewVersion.fileName}
          mimeType={previewVersion.mimeType}
          fileSize={previewVersion.fileSize}
          getUrl={() => getVersionDownloadUrl(id, previewVersion.id)}
          streamUrl={`${API_URL}/templates/${id}/versions/${previewVersion.id}/stream`}
          onClose={() => setPreviewVersion(null)}
        />
      )}
    </div>
  );
}
